import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  ChevronLeft, Send, MessageSquare, Mail, Bell, Smartphone, AlertTriangle,
  History, Eye, Users as UsersIcon,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ROLE_OPTIONS = [
  { value: 'resident', label: 'Residents' },
  { value: 'trustee', label: 'Trustees' },
  { value: 'chairman', label: 'Chairmen' },
  { value: 'property_manager', label: 'Property Managers' },
  { value: 'managing_agent', label: 'Managing Agents' },
];

const CATEGORY_OPTIONS = [
  { value: 'notice', label: 'Notice', icon: Bell },
  { value: 'emergency', label: 'Emergency', icon: AlertTriangle },
  { value: 'payment_reminder', label: 'Payment Reminder', icon: Mail },
  { value: 'maintenance', label: 'Maintenance', icon: MessageSquare },
  { value: 'general', label: 'General', icon: MessageSquare },
];

const TEMPLATES = {
  emergency: {
    subject: 'Emergency Alert — Action Required',
    body: 'Dear residents,\n\nWe have an emergency situation:\n\n[Describe situation]\n\nPlease take the following action:\n[Actions]\n\nThank you.',
  },
  payment_reminder: {
    subject: 'Friendly Reminder: Levy Payment Due',
    body: 'Dear resident,\n\nThis is a reminder that your levy payment is due. Please log in to BodyCorp365 to view your statement and settle the outstanding amount.\n\nThank you.',
  },
  maintenance: {
    subject: 'Scheduled Maintenance Notice',
    body: 'Dear residents,\n\nScheduled maintenance is planned for [date/time]. This may affect [area/service]. We apologise for any inconvenience.\n\nThank you for your patience.',
  },
  notice: {
    subject: 'New Notice from Building Management',
    body: 'Dear residents,\n\n[Type your notice here]\n\nKind regards,\nBuilding Management',
  },
};

export default function PlatformCommunications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Compose form state
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('notice');
  const [channels, setChannels] = useState(new Set(['email', 'inapp']));
  const [targetHoaIds, setTargetHoaIds] = useState(() => {
    const fromUrl = searchParams.get('hoas');
    return fromUrl ? fromUrl.split(',') : [];
  });
  const [targetRoles, setTargetRoles] = useState(new Set());
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  // Building picker
  const [buildings, setBuildings] = useState([]);
  const [buildingSearch, setBuildingSearch] = useState('');

  // History
  const [history, setHistory] = useState([]);

  const fetchBuildings = useCallback(async () => {
    const r = await fetch(`${API}/api/platform-admin/buildings?limit=300`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (r.ok) setBuildings((await r.json()).buildings || []);
  }, []);

  const fetchHistory = useCallback(async () => {
    const r = await fetch(`${API}/api/platform-admin/communications/history?limit=100`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (r.ok) setHistory((await r.json()).history || []);
  }, []);

  useEffect(() => { fetchBuildings(); fetchHistory(); }, [fetchBuildings, fetchHistory]);

  const toggleChannel = (c) => {
    const n = new Set(channels);
    n.has(c) ? n.delete(c) : n.add(c);
    setChannels(n);
  };
  const toggleRole = (r) => {
    const n = new Set(targetRoles);
    n.has(r) ? n.delete(r) : n.add(r);
    setTargetRoles(n);
  };
  const toggleBuilding = (id) => {
    setTargetHoaIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const applyTemplate = (cat) => {
    setCategory(cat);
    const t = TEMPLATES[cat];
    if (t) {
      setSubject(t.subject);
      setBody(t.body);
    }
  };

  const previewRecipients = async () => {
    if (targetHoaIds.length === 0) return toast.error('Select at least one building');
    const params = new URLSearchParams();
    params.set('target_hoa_ids', targetHoaIds.join(','));
    if (targetRoles.size > 0) params.set('target_roles', Array.from(targetRoles).join(','));
    const r = await fetch(`${API}/api/platform-admin/communications/preview-recipients?${params}`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (r.ok) setPreview(await r.json());
    else toast.error('Preview failed');
  };

  const sendCommunication = async (testMode = false) => {
    if (!subject.trim() || !body.trim()) return toast.error('Subject and body are required');
    if (targetHoaIds.length === 0) return toast.error('Select at least one building');
    if (channels.size === 0) return toast.error('Select at least one delivery channel');
    setSending(true);
    const r = await fetch(`${API}/api/platform-admin/communications/send`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({
        subject, body, category,
        channels: Array.from(channels),
        target_hoa_ids: targetHoaIds,
        target_roles: targetRoles.size > 0 ? Array.from(targetRoles) : null,
        test_mode: testMode,
      }),
    });
    setSending(false);
    if (r.ok) {
      const data = await r.json();
      toast.success(testMode ? `Test sent to your inbox (1 recipient)` : `Sent to ${data.recipient_count} recipient(s)`);
      if (!testMode) {
        setSubject(''); setBody(''); setPreview(null);
        fetchHistory();
      }
    } else {
      const err = await r.json().catch(() => ({}));
      const msg = typeof err.detail === 'string' ? err.detail : (err.detail?.message || 'Send failed');
      if (r.status === 429) toast.error(msg, { duration: 8000, description: 'Rate limit: max 5 mass messages per hour per building.' });
      else toast.error(msg);
    }
  };

  const filteredBuildings = useMemo(() => {
    if (!buildingSearch) return buildings;
    const q = buildingSearch.toLowerCase();
    return buildings.filter((b) =>
      (b.name || '').toLowerCase().includes(q) || (b.city || '').toLowerCase().includes(q),
    );
  }, [buildings, buildingSearch]);

  if (user?.role !== 'platform_owner') {
    return <div className="p-8 text-center text-gray-500">Platform Owner access required.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8" data-testid="communications-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded hover:bg-gray-200">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Send className="w-7 h-7 text-indigo-600" /> Communication Center
            </h1>
            <p className="text-gray-600 text-sm">Send mass notices, emergency alerts and reminders across the platform.</p>
          </div>
        </div>

        <Tabs defaultValue="compose">
          <TabsList>
            <TabsTrigger value="compose" data-testid="tab-compose"><Send className="w-4 h-4 mr-1" />Compose</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history"><History className="w-4 h-4 mr-1" />History</TabsTrigger>
          </TabsList>

          <TabsContent value="compose" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Compose form */}
              <Card className="lg:col-span-2">
                <CardContent className="p-5 space-y-4">
                  <div>
                    <Label>Category</Label>
                    <Select value={category} onValueChange={applyTemplate}>
                      <SelectTrigger data-testid="category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORY_OPTIONS.map(({ value, label }) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Subject *</Label>
                    <Input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="e.g. Water maintenance Saturday 9am-2pm"
                      data-testid="subject-input"
                    />
                  </div>

                  <div>
                    <Label>Message *</Label>
                    <Textarea
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      rows={8}
                      placeholder="Type your message..."
                      data-testid="body-input"
                    />
                  </div>

                  <div>
                    <Label className="mb-2 block">Delivery channels</Label>
                    <div className="flex flex-wrap gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox checked={channels.has('email')} onCheckedChange={() => toggleChannel('email')} data-testid="ch-email" />
                        <Mail className="w-4 h-4" /> Email
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox checked={channels.has('inapp')} onCheckedChange={() => toggleChannel('inapp')} data-testid="ch-inapp" />
                        <Bell className="w-4 h-4" /> In-app
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox checked={channels.has('push')} onCheckedChange={() => toggleChannel('push')} data-testid="ch-push" />
                        <Smartphone className="w-4 h-4" /> Push
                      </label>
                    </div>
                  </div>

                  <div>
                    <Label className="mb-2 block">Target roles (optional — empty = everyone in building)</Label>
                    <div className="flex flex-wrap gap-2">
                      {ROLE_OPTIONS.map(({ value, label }) => (
                        <label key={value} className="flex items-center gap-1 px-3 py-1 border rounded-full cursor-pointer text-sm hover:bg-gray-50">
                          <Checkbox checked={targetRoles.has(value)} onCheckedChange={() => toggleRole(value)} data-testid={`role-${value}`} />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {preview && (
                    <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm" data-testid="recipient-preview">
                      <div className="font-medium flex items-center gap-2"><UsersIcon className="w-4 h-4" /> {preview.count} recipient(s) will receive this</div>
                      {preview.sample.length > 0 && (
                        <div className="mt-1 text-xs text-gray-600">
                          Sample: {preview.sample.slice(0, 5).map((s) => s.email).join(', ')}
                          {preview.count > 5 && ` and ${preview.count - 5} more`}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 pt-3 border-t">
                    <Button variant="outline" onClick={previewRecipients} data-testid="preview-btn">
                      <Eye className="w-4 h-4 mr-1" /> Preview Recipients
                    </Button>
                    <Button variant="outline" onClick={() => sendCommunication(true)} disabled={sending}>
                      Send Test to Me
                    </Button>
                    <Button onClick={() => sendCommunication(false)} disabled={sending} className="ml-auto" data-testid="send-btn">
                      <Send className="w-4 h-4 mr-1" />
                      {sending ? 'Sending…' : `Send to ${preview?.count || '?'} Recipients`}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Building picker */}
              <Card>
                <CardContent className="p-5">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <UsersIcon className="w-4 h-4" /> Target Buildings ({targetHoaIds.length})
                  </h3>
                  <Input
                    value={buildingSearch}
                    onChange={(e) => setBuildingSearch(e.target.value)}
                    placeholder="Search buildings..."
                    className="mb-2"
                    data-testid="building-search"
                  />
                  <div className="max-h-[400px] overflow-y-auto border rounded">
                    {filteredBuildings.map((b) => (
                      <label
                        key={b.hoa_id}
                        className="flex items-center gap-2 px-2 py-1.5 border-b last:border-0 cursor-pointer hover:bg-gray-50 text-sm"
                      >
                        <Checkbox
                          checked={targetHoaIds.includes(b.hoa_id)}
                          onCheckedChange={() => toggleBuilding(b.hoa_id)}
                          data-testid={`pick-${b.hoa_id}`}
                        />
                        <div className="flex-1 truncate">
                          <div className="font-medium truncate">{b.name}</div>
                          <div className="text-xs text-gray-500">{b.city} · {b.user_count} users</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Channels</TableHead>
                      <TableHead>Buildings</TableHead>
                      <TableHead className="text-right">Recipients</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">No messages sent yet.</TableCell></TableRow>
                    ) : history.map((h) => (
                      <TableRow key={h.communication_id} data-testid={`history-${h.communication_id}`}>
                        <TableCell className="text-xs whitespace-nowrap">{new Date(h.created_at).toLocaleString()}</TableCell>
                        <TableCell className="font-medium max-w-xs truncate">{h.subject}</TableCell>
                        <TableCell><Badge variant="outline" className="capitalize">{h.category}</Badge></TableCell>
                        <TableCell className="text-xs">{(h.channels || []).join(', ')}</TableCell>
                        <TableCell className="text-xs">{(h.target_hoa_ids || []).length}</TableCell>
                        <TableCell className="text-right">{h.recipient_count}</TableCell>
                        <TableCell><Badge variant="outline">{h.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
